import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Search, X, Loader2, Save, FileText, BookOpen } from 'lucide-react';
import api, { examService } from '../../../services/api';
import LightningIcon from './icons/LightningIcon';
import ClockIcon from './icons/ClockIcon';

const CATEGORY_LABELS = {
    'droitAerien': { fr: 'Droit Aérien', en: 'Air Law' },
    'meteo': { fr: 'Météo', en: 'Meteo' },
    'navigation': { fr: 'Navigation', en: 'Navigation' },
    'connaissance': { fr: 'Conn. Générale', en: 'Gen. Knowledge' },
    'complet': { fr: 'Examen complet', en: 'Full Exam' },
    'other': { fr: 'Autre', en: 'Other' }
};

const KeywordExamSection = ({ levelId, language, onStartExam, requiresUpgrade = false, onUpgrade }) => {
    const [inputValue, setInputValue] = useState('');
    const [keywords, setKeywords] = useState([]);
    const [operator, setOperator] = useState('OR');
    const [searchMode, setSearchMode] = useState('questions'); // 'questions' | 'exams'
    const [searching, setSearching] = useState(false);
    const [loadingPageIdx, setLoadingPageIdx] = useState(null);
    const [loadingExamId, setLoadingExamId] = useState(null);
    const [searchResult, setSearchResult] = useState(null); // questions mode result
    const [examResult, setExamResult] = useState(null); // exams mode result
    const [error, setError] = useState(null);
    const [presets, setPresets] = useState([]);
    const [showModeModal, setShowModeModal] = useState(null); // { pageIdx, questionIds } or { examId }
    const [presetName, setPresetName] = useState('');
    const [showSavePreset, setShowSavePreset] = useState(false);
    const [savingPreset, setSavingPreset] = useState(false);
    const inputRef = useRef(null);

    // Load presets from DB
    useEffect(() => {
        api.getCustomExamPresets(levelId, 'keyword').then(setPresets).catch(() => {});
    }, [levelId]);

    const handleSavePreset = useCallback(async () => {
        const name = presetName.trim();
        if (!name || !searchResult) return;
        setSavingPreset(true);
        try {
            await api.saveCustomExamPreset({
                levelId,
                presetType: 'keyword',
                name,
                config: { keywords: searchResult.keywords, operator: searchResult.operator },
                resultCount: searchResult.totalQuestions
            });
            setPresetName('');
            setShowSavePreset(false);
            // Refresh presets
            const updated = await api.getCustomExamPresets(levelId, 'keyword');
            setPresets(updated);
        } catch { /* ignore */ }
        setSavingPreset(false);
    }, [presetName, searchResult, levelId]);

    const handleDeletePreset = useCallback(async (presetId) => {
        try {
            await api.deleteCustomExamPreset(presetId);
            setPresets(prev => prev.filter(p => p.id !== presetId));
        } catch { /* ignore */ }
    }, []);

    // Detect if a word is likely plural and suggest singular
    const getSingularSuggestion = useCallback((word) => {
        if (!word || word.length < 3) return null;
        const lowerWord = word.toLowerCase();

        // French rules
        if (lowerWord.endsWith('eaux')) {
            return word.slice(0, -4) + 'eau'; // bateaux → bateau
        }
        if (lowerWord.endsWith('aux') && !lowerWord.endsWith('eaux')) {
            return word.slice(0, -3) + 'al'; // chevaux → cheval
        }
        if (lowerWord.endsWith('eux') && lowerWord.length > 4) {
            return word.slice(0, -1); // dangereux stays, but jeux → jeu
        }
        // Common 's' plural (French & English)
        if (lowerWord.endsWith('s') && !lowerWord.endsWith('ss') && !lowerWord.endsWith('us')) {
            return word.slice(0, -1); // avions → avion, planes → plane
        }
        return null;
    }, []);

    // Current suggestion for the input
    const currentSuggestion = inputValue.trim() ? getSingularSuggestion(inputValue.trim()) : null;

    const addKeyword = useCallback((kwOverride) => {
        const kw = (kwOverride || inputValue).trim();
        if (kw && !keywords.includes(kw)) {
            setKeywords(prev => [...prev, kw]);
        }
        setInputValue('');
        inputRef.current?.focus();
    }, [inputValue, keywords]);

    // Replace a keyword with its singular form
    const replaceWithSingular = useCallback((plural, singular) => {
        setKeywords(prev => prev.map(k => k === plural ? singular : k));
    }, []);

    const removeKeyword = useCallback((kw) => {
        setKeywords(prev => prev.filter(k => k !== kw));
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addKeyword();
        } else if (e.key === 'Backspace' && !inputValue && keywords.length > 0) {
            setKeywords(prev => prev.slice(0, -1));
        }
    }, [addKeyword, inputValue, keywords.length]);

    const doSearchQuestions = useCallback(async (kws = keywords, op = operator) => {
        if (kws.length === 0) return;
        setSearching(true);
        setError(null);
        setSearchResult(null);
        setExamResult(null);
        setShowSavePreset(false);

        try {
            const result = await api.searchKeywordQuestions(kws, levelId, language, op);
            const searchData = { ...result, keywords: kws, operator: op };
            setSearchResult(searchData);
        } catch (err) {
            setError(language === 'fr'
                ? 'Erreur lors de la recherche. Veuillez réessayer.'
                : 'Search error. Please try again.');
        } finally {
            setSearching(false);
        }
    }, [keywords, operator, levelId, language]);

    const doSearchExams = useCallback(async (kws = keywords, op = operator) => {
        if (kws.length === 0) return;
        setSearching(true);
        setError(null);
        setSearchResult(null);
        setExamResult(null);
        setShowSavePreset(false);

        try {
            const result = await api.searchKeywordExams(kws, levelId, language, op);
            setExamResult({ ...result, keywords: kws, operator: op });
        } catch (err) {
            setError(language === 'fr'
                ? 'Erreur lors de la recherche. Veuillez réessayer.'
                : 'Search error. Please try again.');
        } finally {
            setSearching(false);
        }
    }, [keywords, operator, levelId, language]);

    const handleSearch = useCallback(() => {
        // Add pending input first
        let kws = [...keywords];
        const pending = inputValue.trim();
        if (pending && !kws.includes(pending)) {
            kws.push(pending);
            setKeywords(kws);
            setInputValue('');
        }
        if (searchMode === 'questions') {
            doSearchQuestions(kws);
        } else {
            doSearchExams(kws);
        }
    }, [keywords, inputValue, searchMode, doSearchQuestions, doSearchExams]);

    const handlePresetClick = useCallback((preset) => {
        const config = preset.config;
        setKeywords(config.keywords || []);
        setOperator(config.operator || 'OR');
        if (searchMode === 'questions') {
            doSearchQuestions(config.keywords || [], config.operator || 'OR');
        } else {
            doSearchExams(config.keywords || [], config.operator || 'OR');
        }
    }, [searchMode, doSearchQuestions, doSearchExams]);

    const handleStartQuestionExam = useCallback(async (pageIdx) => {
        const page = searchResult?.pages?.[pageIdx];
        if (!page) return;
        setShowModeModal({ type: 'questions', pageIdx, questionIds: page.questionIds });
    }, [searchResult]);

    const handleStartTargetedExam = useCallback((exam) => {
        setShowModeModal({ type: 'exam', exam });
    }, []);

    const handleModeSelected = useCallback(async (mode) => {
        if (!showModeModal) return;

        if (showModeModal.type === 'questions') {
            const { pageIdx, questionIds } = showModeModal;
            setShowModeModal(null);
            setLoadingPageIdx(pageIdx);

            try {
                const questions = await api.getQuestionsByIds(questionIds);
                if (questions.length === 0) {
                    setError(language === 'fr'
                        ? 'Aucune question trouvée.'
                        : 'No questions found.');
                    return;
                }
                onStartExam(questions, mode, {
                    examType: 'keyword',
                    config: { keywords: searchResult?.keywords || keywords, operator: searchResult?.operator || operator },
                    pageIndex: pageIdx
                });
            } catch (err) {
                setError(language === 'fr'
                    ? 'Erreur lors du chargement des questions.'
                    : 'Error loading questions.');
            } finally {
                setLoadingPageIdx(null);
            }
        } else if (showModeModal.type === 'exam') {
            // Start a regular exam (redirect to exam session)
            const { exam } = showModeModal;
            setShowModeModal(null);
            setLoadingExamId(exam.id);

            try {
                // Fetch exam questions using examService
                const questions = await examService.getQuestionsByExam(levelId, exam.category, exam.id);
                if (questions.length === 0) {
                    setError(language === 'fr'
                        ? 'Aucune question trouvée.'
                        : 'No questions found.');
                    return;
                }
                onStartExam(questions, mode, {
                    examType: 'keyword-targeted',
                    config: { keywords: examResult?.keywords || keywords, operator: examResult?.operator || operator, targetedExamId: exam.id },
                    pageIndex: 0,
                    annexeExamId: exam.hasAnnexe ? exam.id : null
                });
            } catch (err) {
                setError(language === 'fr'
                    ? 'Erreur lors du chargement de l\'examen.'
                    : 'Error loading exam.');
            } finally {
                setLoadingExamId(null);
            }
        }
    }, [showModeModal, language, onStartExam, searchResult, examResult, keywords, operator]);

    const clearResults = useCallback(() => {
        setSearchResult(null);
        setExamResult(null);
        setKeywords([]);
    }, []);

    const getLocalizedText = (textObj) => {
        if (!textObj) return '';
        if (typeof textObj === 'string') return textObj;
        return textObj[language] || textObj['en'] || '';
    };

    const getCategoryLabel = (cat) => {
        const label = CATEGORY_LABELS[cat];
        return label ? getLocalizedText(label) : cat;
    };

    const kwLabel = searchResult?.keywords?.join(', ') || examResult?.keywords?.join(', ') || keywords.join(', ');

    return (
        <div data-help="custom-keywords" className="space-y-4">
            <h2 className="text-lg font-black text-gray-400 dark:text-white ml-2 text-xs uppercase tracking-widest opacity-60 dark:opacity-40">
                {language === 'fr' ? 'Mots-clés' : 'Keywords'}
            </h2>

            {/* Search Bar */}
            <div className="bg-white dark:bg-[#121212] rounded-2xl p-5 border border-gray-200 dark:border-white/5 shadow-sm dark:shadow-none">
                <div className="flex flex-col gap-3">
                    {/* Input with chips */}
                    <div className="flex items-center gap-2 flex-wrap bg-gray-50 dark:bg-white/5 rounded-xl px-3 py-2 border border-gray-200 dark:border-white/10 focus-within:border-blue-400 dark:focus-within:border-blue-500 transition">
                        <Search className="w-4 h-4 text-gray-400 shrink-0" />
                        {keywords.map((kw) => {
                            const singular = getSingularSuggestion(kw);
                            const showReplaceSuggestion = singular && operator === 'AND' && !keywords.includes(singular);
                            return (
                                <span key={kw} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-semibold">
                                    {kw}
                                    {showReplaceSuggestion && (
                                        <button
                                            onClick={() => replaceWithSingular(kw, singular)}
                                            className="ml-0.5 px-1 py-0.5 rounded bg-amber-200 dark:bg-amber-700/50 text-amber-700 dark:text-amber-300 hover:bg-amber-300 dark:hover:bg-amber-600/50 transition text-[10px] font-bold"
                                            title={language === 'fr' ? `Remplacer par "${singular}"` : `Replace with "${singular}"`}
                                        >
                                            →{singular}
                                        </button>
                                    )}
                                    <button onClick={() => removeKeyword(kw)} className="hover:text-blue-900 dark:hover:text-white transition">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            );
                        })}
                        <input
                            ref={inputRef}
                            type="text"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={keywords.length === 0
                                ? (language === 'fr' ? 'Tapez un mot-clef puis Entrée...' : 'Type a keyword then Enter...')
                                : (language === 'fr' ? 'Ajouter...' : 'Add...')}
                            className="flex-1 min-w-[120px] bg-transparent outline-none text-sm text-gray-900 dark:text-white placeholder-gray-400"
                        />
                    </div>

                    {/* Singular suggestion for current input */}
                    {currentSuggestion && !keywords.includes(currentSuggestion) && (
                        <div className="flex items-center gap-2 text-xs">
                            <span className="text-gray-400">
                                {language === 'fr' ? 'Suggestion :' : 'Suggestion:'}
                            </span>
                            <button
                                onClick={() => addKeyword(currentSuggestion)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition font-medium"
                            >
                                + {currentSuggestion}
                                <span className="text-amber-400 dark:text-amber-500 text-[10px]">
                                    ({language === 'fr' ? 'singulier' : 'singular'})
                                </span>
                            </button>
                        </div>
                    )}

                    {/* Search mode toggle */}
                    <div>
                        <p className="text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-2">
                            {language === 'fr' ? 'Type de recherche' : 'Search type'}
                        </p>
                        <div className="flex gap-2">
                            <button
                                onClick={() => { setSearchMode('questions'); setSearchResult(null); setExamResult(null); }}
                                className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-bold transition-all text-left ${
                                    searchMode === 'questions'
                                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-2 border-blue-400 dark:border-blue-600'
                                        : 'bg-gray-50 dark:bg-white/5 text-gray-500 dark:text-gray-400 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-white/10'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    {language === 'fr' ? 'Questions sur mesure' : 'Custom questions'}
                                </div>
                                <div className={`text-xs font-normal mt-0.5 ${searchMode === 'questions' ? 'text-blue-600/70 dark:text-blue-400/60' : 'text-gray-400 dark:text-gray-500'}`}>
                                    {language === 'fr' ? 'Les questions utilisant les annexes ne seront pas incluses · Découpage en examens de 50 questions maximum' : 'Questions using appendices will not be included · Split into exams of 50 questions max'}
                                </div>
                            </button>
                            <button
                                onClick={() => { setSearchMode('exams'); setSearchResult(null); setExamResult(null); }}
                                className={`flex-1 px-3 py-2.5 rounded-xl text-sm font-bold transition-all text-left ${
                                    searchMode === 'exams'
                                        ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-2 border-violet-400 dark:border-violet-600'
                                        : 'bg-gray-50 dark:bg-white/5 text-gray-500 dark:text-gray-400 border-2 border-transparent hover:bg-gray-100 dark:hover:bg-white/10'
                                }`}
                            >
                                <div className="flex items-center gap-2">
                                    <BookOpen className="w-4 h-4" />
                                    {language === 'fr' ? 'Examens ciblés' : 'Targeted exams'}
                                </div>
                                <div className={`text-xs font-normal mt-0.5 ${searchMode === 'exams' ? 'text-violet-600/70 dark:text-violet-400/60' : 'text-gray-400 dark:text-gray-500'}`}>
                                    {language === 'fr' ? 'Examens complets avec annexes' : 'Full exams with appendices'}
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Controls row */}
                    <div className="flex items-center justify-between gap-3">
                        {/* AND/OR toggle */}
                        <button
                            onClick={() => setOperator(prev => prev === 'OR' ? 'AND' : 'OR')}
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-bold border transition hover:bg-gray-50 dark:hover:bg-white/5"
                            style={{
                                borderColor: operator === 'AND' ? '#8B5CF6' : '#3B82F6',
                                color: operator === 'AND' ? '#8B5CF6' : '#3B82F6'
                            }}
                        >
                            <span className="opacity-60">{language === 'fr' ? 'Mode' : 'Mode'}:</span>
                            {operator === 'OR'
                                ? (language === 'fr' ? 'OU (plus large)' : 'OR (broader)')
                                : (language === 'fr' ? 'ET (plus précis)' : 'AND (narrower)')}
                        </button>

                        {/* Search / Unlock button */}
                        {requiresUpgrade ? (
                            <button
                                onClick={onUpgrade}
                                className="px-5 py-2 rounded-xl font-bold text-sm text-white shadow-lg transition bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 flex items-center gap-2"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                {language === 'fr' ? 'Débloquer' : 'Unlock'}
                            </button>
                        ) : (
                            <button
                                onClick={handleSearch}
                                disabled={keywords.length === 0 && !inputValue.trim()}
                                className={`px-5 py-2 rounded-xl font-bold text-sm text-white shadow-lg transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 ${
                                    searchMode === 'exams'
                                        ? 'bg-violet-500 hover:bg-violet-600'
                                        : 'bg-[#007AFF] hover:bg-[#0062CC]'
                                }`}
                            >
                                {searching ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Search className="w-4 h-4" />
                                )}
                                {language === 'fr' ? 'Chercher' : 'Search'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Saved presets & Save button */}
                {(presets.length > 0 || (searchResult && searchResult.totalQuestions > 0)) && (
                    <div className="mt-4 pt-3 border-t border-gray-100 dark:border-white/5 space-y-3">
                        {/* Saved presets - always visible when they exist */}
                        {presets.length > 0 && (
                            <div>
                                <p className="text-xs uppercase tracking-wider font-bold text-gray-400 dark:text-gray-500 mb-2">
                                    {language === 'fr' ? 'Recherches sauvegardées' : 'Saved searches'}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {presets.map((preset) => (
                                        <div key={preset.id} className="inline-flex items-center gap-1">
                                            <button
                                                onClick={() => handlePresetClick(preset)}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-sm font-medium text-gray-700 dark:text-gray-300 transition"
                                            >
                                                <Search className="w-3 h-3 opacity-40" />
                                                {preset.name}
                                                <span className="text-gray-400 dark:text-gray-500">({preset.lastResultCount}q)</span>
                                            </button>
                                            <button
                                                onClick={() => handleDeletePreset(preset.id)}
                                                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-white/10 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Save preset button (after successful search) */}
                        {searchResult && searchResult.totalQuestions > 0 && (
                            <>
                                {showSavePreset ? (
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="text"
                                            value={presetName}
                                            onChange={(e) => setPresetName(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                                            placeholder={language === 'fr' ? 'Nom du preset...' : 'Preset name...'}
                                            className="flex-1 px-3 py-1.5 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-sm text-gray-900 dark:text-white placeholder-gray-400 outline-none focus:border-blue-400"
                                            autoFocus
                                        />
                                        <button
                                            onClick={handleSavePreset}
                                            disabled={!presetName.trim() || savingPreset}
                                            className="px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-bold disabled:opacity-40 hover:bg-blue-600 transition"
                                        >
                                            {savingPreset ? <Loader2 className="w-3 h-3 animate-spin" /> : (language === 'fr' ? 'Enregistrer' : 'Save')}
                                        </button>
                                        <button
                                            onClick={() => setShowSavePreset(false)}
                                            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400 transition"
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => { setShowSavePreset(true); setPresetName(searchResult.keywords?.join(', ') || ''); }}
                                        className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition"
                                    >
                                        <Save className="w-3.5 h-3.5" />
                                        {language === 'fr' ? 'Sauvegarder cette recherche' : 'Save this search'}
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Error */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-xl p-3 text-sm text-red-600 dark:text-red-400">
                    {error}
                </div>
            )}

            {/* Questions Search Results */}
            {searchResult && (
                <div className="space-y-3">
                    {/* Results header */}
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                            <span className="font-bold text-gray-900 dark:text-white">{searchResult.totalQuestions}</span>
                            {' '}{language === 'fr' ? 'questions trouvées' : 'questions found'}
                            {searchResult.totalPages > 1 && (
                                <span className="text-gray-400"> — {searchResult.totalPages} {language === 'fr' ? 'examens' : 'exams'}</span>
                            )}
                        </p>
                        <button
                            onClick={clearResults}
                            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                        >
                            {language === 'fr' ? 'Effacer' : 'Clear'}
                        </button>
                    </div>

                    {searchResult.totalQuestions === 0 ? (
                        <div className="bg-white dark:bg-[#121212] rounded-2xl p-6 border border-gray-200 dark:border-white/5 text-center">
                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                                {language === 'fr'
                                    ? 'Aucune question ne correspond à ces mots-clés.'
                                    : 'No questions match these keywords.'}
                            </p>
                        </div>
                    ) : (
                        /* Exam cards */
                        searchResult.pages.map((page, idx) => (
                            <div
                                key={idx}
                                className="bg-white dark:bg-[#121212] rounded-2xl p-4 md:p-5 border border-gray-200 dark:border-white/5 flex items-center justify-between group hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-all shadow-sm dark:shadow-none"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-blue-500 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                                        {searchResult.totalPages > 1 ? `${idx + 1}/${searchResult.totalPages}` : (
                                            <Search className="w-5 h-5" />
                                        )}
                                    </div>
                                    <div>
                                        <h4 className="text-sm md:text-base font-bold text-gray-900 dark:text-white">
                                            {kwLabel}
                                            {searchResult.totalPages > 1 && (
                                                <span className="text-gray-400 font-normal ml-2">({idx + 1}/{searchResult.totalPages})</span>
                                            )}
                                        </h4>
                                        <p className="text-sm text-gray-500">
                                            {page.count} {language === 'fr' ? 'questions' : 'questions'}
                                            {' · '}{language === 'fr' ? 'Non comptabilisé' : 'Not scored'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleStartQuestionExam(idx)}
                                    disabled={loadingPageIdx === idx}
                                    className="px-5 py-2 rounded-xl font-bold text-sm text-white shadow-lg transition bg-[#007AFF] hover:bg-[#0062CC] disabled:opacity-60 flex items-center gap-2"
                                >
                                    {loadingPageIdx === idx ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : null}
                                    {language === 'fr' ? 'Commencer' : 'Start'}
                                </button>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Exams Search Results */}
            {examResult && (
                <div className="space-y-4">
                    {/* Results header */}
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                            <span className="font-bold text-gray-900 dark:text-white">{examResult.totalExams}</span>
                            {' '}{language === 'fr' ? 'examens trouvés' : 'exams found'}
                            <span className="text-gray-400"> — {examResult.totalMatches} {language === 'fr' ? 'correspondances' : 'matches'}</span>
                        </p>
                        <button
                            onClick={clearResults}
                            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
                        >
                            {language === 'fr' ? 'Effacer' : 'Clear'}
                        </button>
                    </div>

                    {examResult.totalExams === 0 ? (
                        <div className="bg-white dark:bg-[#121212] rounded-2xl p-6 border border-gray-200 dark:border-white/5 text-center">
                            <p className="text-gray-500 dark:text-gray-400 text-sm">
                                {language === 'fr'
                                    ? 'Aucun examen ne correspond à ces mots-clés.'
                                    : 'No exams match these keywords.'}
                            </p>
                        </div>
                    ) : (
                        /* Exams by category */
                        Object.entries(examResult.byCategory).map(([category, exams]) => (
                            <div key={category} className="bg-white dark:bg-[#121212] rounded-2xl border border-gray-200 dark:border-white/5 shadow-sm dark:shadow-none overflow-hidden">
                                {/* Category header */}
                                <div className="px-5 py-3 bg-gray-50 dark:bg-white/5 border-b border-gray-200 dark:border-white/5">
                                    <h4 className="text-sm font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                        {getCategoryLabel(category)}
                                        <span className="ml-2 text-gray-400 dark:text-gray-500 font-normal">({exams.length})</span>
                                    </h4>
                                </div>
                                {/* Exam list */}
                                <div className="divide-y divide-gray-100 dark:divide-white/5">
                                    {exams.map((exam) => (
                                        <div
                                            key={exam.id}
                                            className="px-5 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-white/5 transition"
                                        >
                                            <div className="flex items-center gap-4">
                                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs shadow-md">
                                                    {exam.matchCount}
                                                </div>
                                                <div>
                                                    <h5 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2">
                                                        {getLocalizedText(exam.title)}
                                                        {exam.hasAnnexe && (
                                                            <span className="px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-bold">
                                                                PDF
                                                            </span>
                                                        )}
                                                    </h5>
                                                    <p className="text-sm text-gray-500">
                                                        {exam.matchCount}/{exam.totalQuestions} {language === 'fr' ? 'questions correspondent' : 'questions match'}
                                                    </p>
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleStartTargetedExam(exam)}
                                                disabled={loadingExamId === exam.id}
                                                className="px-4 py-2 rounded-xl font-bold text-sm text-white shadow-lg transition bg-violet-500 hover:bg-violet-600 disabled:opacity-60 flex items-center gap-2"
                                            >
                                                {loadingExamId === exam.id ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : null}
                                                {language === 'fr' ? 'Démarrer' : 'Start'}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {/* Mode Selection Modal */}
            {showModeModal && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center p-6 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white dark:bg-gray-900 rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-200 dark:border-gray-800 animate-in zoom-in-95 duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                                {language === 'fr' ? 'Choisir le mode' : 'Exam mode selection'}
                            </h3>
                            <button onClick={() => setShowModeModal(null)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition">
                                <svg className="w-6 h-6 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-4">
                            <button
                                onClick={() => handleModeSelected('flash')}
                                className="w-full p-6 bg-emerald-500/10 hover:bg-emerald-500/20 border-2 border-emerald-500/20 hover:border-emerald-500 rounded-2xl transition text-left group"
                            >
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white">
                                        <LightningIcon className="w-6 h-6" />
                                    </div>
                                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                                        {language === 'fr' ? 'Mode question-réponse' : 'Flashcard Mode'}
                                    </span>
                                </div>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">
                                    {language === 'fr'
                                        ? 'Obtenez la bonne réponse et l\'explication à la validation de chaque question.'
                                        : 'Receive the correct answer and explanation after validating each question.'}
                                </p>
                            </button>

                            <button
                                onClick={() => handleModeSelected('classic')}
                                className="w-full p-6 bg-blue-500/10 hover:bg-blue-500/20 border-2 border-blue-500/20 hover:border-blue-500 rounded-2xl transition text-left group"
                            >
                                <div className="flex items-center gap-4 mb-2">
                                    <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center text-white">
                                        <ClockIcon className="w-6 h-6" />
                                    </div>
                                    <span className="text-xl font-bold text-gray-900 dark:text-white">
                                        {language === 'fr' ? 'Mode examen' : 'Exam Mode'}
                                    </span>
                                </div>
                                <p className="text-gray-500 dark:text-gray-400 text-sm">
                                    {language === 'fr'
                                        ? 'Validez toutes les questions avant d\'obtenir les bonnes réponses et les explications.'
                                        : 'Validate all the questions before receiving the correct answers and explanations.'}
                                </p>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default KeywordExamSection;
